import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { roleName } from '../config/roleNames';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Users, UserPlus, Trash2, KeyRound, Loader2, Search, ShieldCheck,
  Eye, Phone, MapPin, CalendarClock, UserCircle2, UserX, UserCheck, Lock,
  ArrowUp, ArrowDown, ChevronsUpDown, Settings, Check, X,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const roleLabel = (u) => roleName(u?.roles?.[0]?.role_type);

// Single source for status badge label + colour (no literals in the row markup).
const STATUS_META = {
  active:   { label: 'Active',   cls: 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30' },
  inactive: { label: 'Inactive', cls: 'bg-slate-500/15 text-slate-400 border-slate-500/30' },
  pending:  { label: 'Pending',  cls: 'bg-amber-500/15 text-amber-500 border-amber-500/30' },
  rejected: { label: 'Rejected', cls: 'bg-red-500/15 text-red-500 border-red-500/30' },
};

/**
 * MemberMgmtPanel — top-level "Member Mgmt" tab (System Admin + Tech Specialist).
 * Full user-management console backed by the OL `users` collection (OL is the
 * SOT — NOT the WordPress user table). Add / edit / delete users and set
 * passwords directly.
 */
export default function MemberMgmtPanel({ orgId }) {
  const { isDark } = useTheme();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState(['family']);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [sort, setSort] = useState({ key: 'last_login', dir: 'desc' });
  const [showAdd, setShowAdd] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);

  const card = isDark ? 'bg-slate-800/60 border-slate-700' : 'bg-white border-slate-200';
  const muted = isDark ? 'text-slate-400' : 'text-slate-500';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/api/member-mgmt/${orgId}/users`, { withCredentials: true });
      setUsers(data.users || []);
      setSelected(new Set());
      if (data.assignable_roles) setRoles(data.assignable_roles);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load members');
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { if (orgId) load(); }, [orgId, load]);

  const memberRole = (u) => {
    const rt = u?.roles?.[0]?.role_type;
    return rt === 'family' ? 'Member' : (roleName(rt) || 'Member');
  };

  const filtered = users.filter((u) => {
    if (statusFilter !== 'all') {
      const appr = u.approval_status || 'approved';
      if (statusFilter === 'pending') { if (appr !== 'pending') return false; }
      else if (statusFilter === 'rejected') { if (appr !== 'rejected') return false; }
      else if ((u.status || 'active') !== statusFilter) return false;
    }
    if (roleFilter !== 'all' && memberRole(u) !== roleFilter) return false;
    if (q.trim()) {
      const n = q.trim().toLowerCase();
      if (!(`${u.first_name} ${u.last_name}`.toLowerCase().includes(n)
        || (u.email || '').toLowerCase().includes(n))) return false;
    }
    return true;
  });

  const toggleSort = (key) => setSort((s) =>
    s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const isPending = (u) => (u.approval_status || 'approved') === 'pending';
  const sorted = [...filtered].sort((a, b) => {
    // Pending applicants are ALWAYS pinned on top (they need an approve/reject
    // decision), regardless of the active column sort.
    const ap = isPending(a), bp = isPending(b);
    if (ap !== bp) return ap ? -1 : 1;
    let av, bv;
    if (sort.key === 'name') {
      av = `${a.first_name} ${a.last_name}`.trim().toLowerCase();
      bv = `${b.first_name} ${b.last_name}`.trim().toLowerCase();
    } else { // last_login
      av = a.last_login ? new Date(a.last_login).getTime() : -Infinity;
      bv = b.last_login ? new Date(b.last_login).getTime() : -Infinity;
    }
    if (av < bv) return sort.dir === 'asc' ? -1 : 1;
    if (av > bv) return sort.dir === 'asc' ? 1 : -1;
    return 0;
  });

  const SortHeader = ({ label, sortKey, className }) => (
    <th className={className}>
      <button onClick={() => toggleSort(sortKey)}
              className="inline-flex items-center gap-1 hover:text-teal-400 transition-colors"
              data-testid={`member-sort-${sortKey}`}>
        {label}
        {sort.key === sortKey
          ? (sort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />)
          : <ChevronsUpDown className="h-3.5 w-3.5 opacity-40" />}
      </button>
    </th>
  );
  const allChecked = filtered.length > 0 && filtered.every((u) => selected.has(u.id));
  const toggleAll = () => {
    const next = new Set(selected);
    if (allChecked) filtered.forEach((u) => next.delete(u.id));
    else filtered.forEach((u) => next.add(u.id));
    setSelected(next);
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const del = async (u) => {    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/member-mgmt/${orgId}/users/${u.id}`, { withCredentials: true });
      toast.success('Member deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };

  const bulk = async (action) => {
    const ids = [...selected];
    if (!ids.length) return;
    const verb = action === 'delete' ? 'permanently delete'
      : action === 'deactivate' ? 'make inactive' : 'restore';
    if (!window.confirm(`Are you sure you want to ${verb} ${ids.length} member(s)?`)) return;
    setBulkBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/api/member-mgmt/${orgId}/users/bulk`, { action, ids }, { withCredentials: true });
      toast.success(`${data.done} member(s) updated${data.skipped ? `, ${data.skipped} skipped` : ''}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk action failed'); }
    finally { setBulkBusy(false); }
  };

  const decide = async (u, approve) => {
    const verb = approve ? 'approve' : 'reject';
    if (!window.confirm(`${approve ? 'Approve' : 'Reject'} ${u.email}? They will be emailed.`)) return;
    try {
      await axios.post(`${API}/api/member-mgmt/${orgId}/users/${u.id}/${verb}`, {}, { withCredentials: true });
      toast.success(`Member ${approve ? 'approved' : 'rejected'} — email sent`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || `${verb} failed`); }
  };

  // Per-row Active/Inactive — reuses the SAME /users/bulk endpoint as the bulk bar.
  const changeStatus = async (u, action) => {
    if (action === 'deactivate' && !window.confirm(`Make ${u.email} inactive? They won't be able to log in.`)) return;
    try {
      await axios.post(`${API}/api/member-mgmt/${orgId}/users/bulk`, { action, ids: [u.id] }, { withCredentials: true });
      toast.success(action === 'activate' ? 'Member set Active' : 'Member set Inactive');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Status change failed'); }
  };

  const th = `py-2 pr-3 font-medium`;

  return (
    <div className="space-y-4" data-testid="member-mgmt-panel">
      <Card className={card}>
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-teal-500/15">
                <Users className="h-5 w-5 text-teal-400" />
              </div>
              <div>
                <CardTitle>WordPress Members</CardTitle>
                <p className={`text-sm ${muted} mt-0.5`}>
                  The website's members (WordPress / UltimateMembers accounts), stored in
                  OmniLeagues — add, remove members and set passwords here. OL staff / operator
                  accounts are managed separately.
                </p>
              </div>
            </div>
            <Button
              onClick={() => setShowAdd(true)}
              className="bg-teal-600 hover:bg-teal-700"
              data-testid="member-add-btn"
            >
              <UserPlus className="h-4 w-4 mr-1.5" /> Add Member
            </Button>
            <Button
              variant="outline" onClick={() => setShowSettings(true)}
              className="ml-2" title="Approval-notification settings"
              data-testid="member-settings-btn"
            >
              <Settings className="h-4 w-4 mr-1.5" /> Settings
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or email…"
                className="pl-8"
                data-testid="member-search-input"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-36" data-testid="member-filter-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending approval</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-32" data-testid="member-filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="Member">Member</SelectItem>
              </SelectContent>
            </Select>
            <span className={`text-xs ${muted} ml-auto`} data-testid="member-count">
              {filtered.length} {filtered.length === 1 ? 'member' : 'members'}
            </span>
          </div>

          {selected.size > 0 && (
            <div className="flex items-center gap-2 p-2 rounded-md bg-teal-500/10 border border-teal-500/30"
                 data-testid="member-bulk-bar">
              <span className="text-sm font-medium">{selected.size} selected</span>
              <div className="ml-auto flex gap-2 flex-wrap">
                <Button size="sm" variant="outline" disabled={bulkBusy}
                        onClick={() => bulk('deactivate')} data-testid="member-bulk-deactivate">
                  <UserX className="h-4 w-4 mr-1" /> Make Inactive
                </Button>
                <Button size="sm" variant="outline" disabled={bulkBusy}
                        onClick={() => bulk('activate')} data-testid="member-bulk-activate">
                  <UserCheck className="h-4 w-4 mr-1" /> Restore
                </Button>
                <Button size="sm" variant="outline" disabled={bulkBusy}
                        onClick={() => bulk('delete')}
                        className="text-red-500 hover:text-red-600 border-red-500/40"
                        data-testid="member-bulk-delete">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-teal-500" />
            </div>
          ) : filtered.length === 0 ? (
            <p className={`text-center py-10 ${muted}`}>No members found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="member-table">
                <thead>
                  <tr className={`text-left ${muted} border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                    <th className="py-2 pr-3 w-8">
                      <input type="checkbox" checked={allChecked} onChange={toggleAll}
                             className="accent-teal-600 h-4 w-4 cursor-pointer"
                             data-testid="member-select-all" aria-label="Select all" />
                    </th>
                    <SortHeader label="Name" sortKey="name" className={th} />
                    <th className={th}>Email</th>
                    <th className={th}>Role</th>
                    <th className={th}>Status</th>
                    <SortHeader label="Last login" sortKey="last_login" className={th} />
                    <th className={`${th} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((u) => {
                    const inactive = (u.status || 'active') === 'inactive';
                    const appr = u.approval_status || 'approved';
                    return (
                    <tr key={u.id}
                        className={`border-b ${isDark ? 'border-slate-800' : 'border-slate-100'} ${inactive ? 'opacity-60' : ''}`}
                        data-testid={`member-row-${u.id}`}>
                      <td className="py-2 pr-3">
                        <input type="checkbox" checked={selected.has(u.id)}
                               onChange={() => toggleOne(u.id)}
                               className="accent-teal-600 h-4 w-4 cursor-pointer"
                               data-testid={`member-check-${u.id}`} aria-label={`Select ${u.email}`} />
                      </td>
                      <td className="py-2 pr-3">
                        <button onClick={() => setDetailUser(u)}
                                className="text-left hover:underline font-medium"
                                data-testid={`member-name-${u.id}`}>
                          {u.first_name} {u.last_name}
                        </button>
                      </td>
                      <td className="py-2 pr-3 font-mono text-xs">{u.email}</td>
                      <td className="py-2 pr-3">
                        <Badge variant="outline" className="text-[10px]" data-testid={`member-role-${u.id}`}>
                          {memberRole(u)}
                        </Badge>
                      </td>
                      <td className="py-2 pr-3">
                        {(() => {
                          const skey = appr === 'pending' ? 'pending' : appr === 'rejected' ? 'rejected' : inactive ? 'inactive' : 'active';
                          const meta = STATUS_META[skey];
                          return (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button type="button"
                                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] uppercase font-medium cursor-pointer hover:opacity-80 transition-opacity ${meta.cls}`}
                                  data-testid={`member-status-${u.id}`} aria-label={`Change status for ${u.email}`}>
                                  {meta.label}<ChevronsUpDown className="h-3 w-3 opacity-70" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="start" className="min-w-[8rem]">
                                {skey === 'pending' && (<>
                                  <DropdownMenuItem onClick={() => decide(u, true)} data-testid={`member-status-approve-${u.id}`}>Approve</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => decide(u, false)} className="text-red-500 focus:text-red-500" data-testid={`member-status-reject-${u.id}`}>Reject</DropdownMenuItem>
                                </>)}
                                {skey === 'rejected' && (
                                  <DropdownMenuItem onClick={() => decide(u, true)} data-testid={`member-status-approve-${u.id}`}>Approve</DropdownMenuItem>
                                )}
                                {(skey === 'active' || skey === 'inactive') && (<>
                                  <DropdownMenuItem onClick={() => changeStatus(u, 'activate')} data-testid={`member-status-active-${u.id}`}>Active</DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => changeStatus(u, 'deactivate')} className="text-slate-400 focus:text-slate-400" data-testid={`member-status-inactive-${u.id}`}>Inactive</DropdownMenuItem>
                                </>)}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          );
                        })()}
                      </td>
                      <td className={`py-2 pr-3 text-xs ${muted}`}>
                        {u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-2 pr-0 text-right whitespace-nowrap">
                        {appr === 'pending' && (
                          <>
                            <Button size="sm" variant="ghost" onClick={() => decide(u, true)}
                                    className="text-emerald-500 hover:text-emerald-600" title="Approve"
                                    data-testid={`member-approve-${u.id}`}>
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => decide(u, false)}
                                    className="text-red-500 hover:text-red-600" title="Reject"
                                    data-testid={`member-reject-${u.id}`}>
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        <Button size="sm" variant="ghost" onClick={() => setDetailUser(u)}
                                title="View details" data-testid={`member-view-${u.id}`}>
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setPwUser(u)}
                                title="Set password" data-testid={`member-setpw-${u.id}`}>
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => del(u)}
                                className="text-red-500 hover:text-red-600" title="Delete"
                                data-testid={`member-del-${u.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddUserDialog open={showAdd} onOpenChange={setShowAdd} orgId={orgId} roles={roles}
                     onDone={() => { setShowAdd(false); load(); }} />
      <NotifySettingsDialog open={showSettings} onOpenChange={setShowSettings} orgId={orgId} />
      <SetPasswordDialog user={pwUser} onOpenChange={(v) => !v && setPwUser(null)} orgId={orgId}
                         onSaved={load} />
      <MemberDetailDialog user={detailUser} onOpenChange={(v) => !v && setDetailUser(null)}
                          isDark={isDark} onSetPassword={(u) => { setDetailUser(null); setPwUser(u); }} />
    </div>
  );
}

function AddUserDialog({ open, onOpenChange, orgId, roles, onDone }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', password: '', role_type: 'family' });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setForm({ first_name: '', last_name: '', email: '', phone: '', password: '', role_type: 'family' });
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/api/member-mgmt/${orgId}/users`, form, { withCredentials: true });
      toast.success('User created');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="member-add-dialog">
        <DialogHeader>
          <DialogTitle>Add Member</DialogTitle>
          <DialogDescription>Creates a website member account, stored in OmniLeagues.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>First name</Label>
              <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                     required data-testid="member-first-name" />
            </div>
            <div>
              <Label>Last name</Label>
              <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                     required data-testid="member-last-name" />
            </div>
          </div>
          <div>
            <Label>Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                   required data-testid="member-email" />
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                   data-testid="member-phone" />
          </div>
          <div>
            <Label>Password (min 8 chars)</Label>
            <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                   required minLength={8} data-testid="member-password" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="member-create-submit">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SetPasswordDialog({ user, onOpenChange, orgId, onSaved }) {
  const [pw, setPw] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setPw(''); }, [user]);
  if (!user) return null;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/api/member-mgmt/${orgId}/users/${user.id}/set-password`,
        { password: pw }, { withCredentials: true });
      toast.success('Password updated — now visible in the member details');
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set password');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent data-testid="member-setpw-dialog">
        <DialogHeader>
          <DialogTitle>Set Password</DialogTitle>
          <DialogDescription>{user.email}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>New password (min 8 chars)</Label>
            <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)}
                   required minLength={8} data-testid="member-newpw-input" />
            <p className="text-xs text-slate-400 mt-1">
              Stored so you can view it later in the member's details. The member logs in with this password.
            </p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="member-newpw-submit">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


function NotifySettingsDialog({ open, onOpenChange, orgId }) {
  const [form, setForm] = useState({ member_admin_name: '', member_admin_email: '' });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios.get(`${API}/api/member-mgmt/${orgId}/settings`, { withCredentials: true })
      .then(({ data }) => setForm({ member_admin_name: data.member_admin_name || '', member_admin_email: data.member_admin_email || '' }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, orgId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/api/member-mgmt/${orgId}/settings`, form, { withCredentials: true });
      toast.success('Notification admin saved');
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="member-settings-dialog">
        <DialogHeader>
          <DialogTitle>Approval Notifications</DialogTitle>
          <DialogDescription>
            When a new member registers on the website, this administrator is emailed so they can
            approve or reject the applicant in Member Mgmt.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-teal-500" /></div>
        ) : (
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Administrator name</Label>
            <Input value={form.member_admin_name}
                   onChange={(e) => setForm({ ...form, member_admin_name: e.target.value })}
                   placeholder="Administrator" data-testid="member-admin-name" />
          </div>
          <div>
            <Label>Administrator email</Label>
            <Input type="email" value={form.member_admin_email}
                   onChange={(e) => setForm({ ...form, member_admin_email: e.target.value })}
                   placeholder="admin@southwakesabres.org" required data-testid="member-admin-email" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="member-settings-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-1.5" />} Save
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-2.5 text-sm">
      <Icon className="h-4 w-4 mt-0.5 text-teal-400 shrink-0" />
      <span className="text-slate-400 w-28 shrink-0">{label}</span>
      <span className="font-medium break-words">{value}</span>
    </div>
  );
}

function MemberDetailDialog({ user, onOpenChange, isDark, onSetPassword }) {
  if (!user) return null;
  const p = user.member_profile || {};
  const b = p.billing || {};
  const fmt = (d) => (d ? new Date(d).toLocaleString() : null);
  const inactive = (user.status || 'active') === 'inactive';
  const role = user?.roles?.[0]?.role_type === 'family'
    ? 'Member' : (roleName(user?.roles?.[0]?.role_type) || 'Member');
  const addr = [b.address_1, b.address_2, [b.city, b.state, b.postcode].filter(Boolean).join(', '), b.country]
    .filter(Boolean).join(' · ');
  return (
    <Dialog open={!!user} onOpenChange={onOpenChange}>
      <DialogContent data-testid="member-detail-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserCircle2 className="h-5 w-5 text-teal-400" />
            {user.first_name} {user.last_name}
            <Badge variant="outline"
                   className={`ml-1 text-[10px] uppercase ${inactive
                     ? 'bg-slate-500/15 text-slate-400 border-slate-500/30'
                     : 'bg-emerald-500/15 text-emerald-500 border-emerald-500/30'}`}>
              {inactive ? 'Inactive' : 'Active'}
            </Badge>
          </DialogTitle>
          <DialogDescription>Website member profile — imported from WordPress into OmniLeagues.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2.5 py-1">
          <Row icon={UserCircle2} label="Role" value={role} />
          <Row icon={UserCircle2} label="Email" value={user.email} />
          <Row icon={Phone} label="Phone" value={user.phone || b.phone} />
          <Row icon={UserCircle2} label="Full name" value={p.full_name} />
          <Row icon={UserCircle2} label="WP username" value={user.wp_login} />
          <Row icon={MapPin} label="Address" value={addr} />
          <Row icon={CalendarClock} label="Registered" value={fmt(user.wp_registered_at)} />
          <Row icon={CalendarClock} label="Last login" value={fmt(user.last_login || p.wp_last_login)} />
          <Row icon={ShieldCheck} label="WP status" value={p.account_status || user.account_status} />
          <Row icon={UserCircle2} label="WP roles" value={(p.wp_roles || []).join(', ')} />
          <Row icon={UserCircle2} label="Bio" value={p.description} />
          {p.photos && (p.photos.profile_photo || p.photos.cover_photo) && (
            <Row icon={UserCircle2} label="Photos"
                 value={[p.photos.profile_photo && 'profile', p.photos.cover_photo && 'cover'].filter(Boolean).join(' + ') + ' (on WordPress)'} />
          )}
          <div className="flex items-start gap-2.5 text-sm pt-1 border-t border-slate-500/20 mt-1">
            <Lock className="h-4 w-4 mt-0.5 text-teal-400 shrink-0" />
            <span className="text-slate-400 w-28 shrink-0">Password</span>
            {user.ol_set_password ? (
              <span className="font-mono font-medium break-all" data-testid="member-detail-password">
                {user.ol_set_password}
              </span>
            ) : (
              <span className="text-slate-400 italic" data-testid="member-detail-password">
                Not set from OL — imported password is encrypted and can't be shown. Use “Set password”.
              </span>
            )}
          </div>
        </div>
        <DialogFooter>
          {onSetPassword && (
            <Button variant="outline" onClick={() => onSetPassword(user)} data-testid="member-detail-setpw">
              <KeyRound className="h-4 w-4 mr-1.5" /> Set password
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
